import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import uniqid from 'uniqid';
import moment from 'moment';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Link as LinkMaterial,
} from '@material-ui/core';
import { SaleDashboardContext } from 'contexts/saleDashboard';
import {
  paymentMethodTitles,
  PAYMENT_BANK_TRANSFER,
  PAYMENT_BARTE,
  PAYMENT_ITAU_BANK_SLIP,
  PAYMENT_PIX_RECURRENT,
  PAYMENT_STATUS_PAID,
  PAYMENT_STATUS_OVERDUE,
  PAYMENT_STATUS_ERROR,
} from 'constants/sales';
import { CANCELLED } from 'constants/common/payments';
import { convertCurrency, localeDate } from 'helpers';
import { fetch as apiFetch } from 'api';
import { showNotification } from 'store/actions/notifications';
import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'constants/notification';
import CopyElement from 'global/CopyElement';

// Formas de pagamento cujo link de cobrança nasce no Asaas (checkout
// pós-assinatura). Espelha AsaasCheckoutService::METHODS na api.
const METODOS_ASAAS = [PAYMENT_ITAU_BANK_SLIP, PAYMENT_BARTE, PAYMENT_PIX_RECURRENT];

const hoje = () => moment().startOf('day');

// Situação de cada parcela: pago, vencido, em aberto ou erro.
const situacao = (payment) => {
  if (payment.status === PAYMENT_STATUS_PAID) {
    return {
      label: payment.payment_date ? `Pago em ${localeDate(payment.payment_date)}` : 'Pago',
      cor: 'primary',
      pago: true,
    };
  }
  if (payment.status === PAYMENT_STATUS_ERROR) {
    return { label: 'Erro na transação', cor: 'secondary', pago: false };
  }
  const vencido = payment.status === PAYMENT_STATUS_OVERDUE
    || (payment.due_date && moment(payment.due_date).isBefore(hoje()));
  if (vencido) return { label: 'Vencido', cor: 'secondary', pago: false };
  return { label: 'Em aberto', cor: 'default', pago: false };
};

// Valor da linha: boleto Itaú e PIX recorrente têm uma linha por parcela;
// cartão (Asaas) é uma cobrança só, parcelada no cartão.
const valorLinha = (payment) => {
  if (payment.payment_method === PAYMENT_BARTE && payment.months > 1) {
    return `${convertCurrency(payment.value)} em ${payment.months}x`;
  }
  return convertCurrency(payment.value);
};

const parcelaLinha = (payment) => {
  if (payment.payment_method === PAYMENT_BARTE) return payment.months > 1 ? `${payment.months}x no cartão` : 'À vista';
  if (payment.months > 1) return `${payment.month_number}/${payment.months}`;
  return 'Única';
};

const ordenar = (a, b) => {
  if (a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
  return (a.month_number || 0) - (b.month_number || 0);
};

function PaymentsStatusDialog({ setUpdate, ...props }) {
  const dashContext = useContext(SaleDashboardContext);
  const { currentClient, dialogPaymentsStatus, handleDialogPaymentsStatus } = dashContext;

  const [payments, setPayments] = useState([]);
  const [linksSentAt, setLinksSentAt] = useState(null);
  const [mostrarPagos, setMostrarPagos] = useState(false);
  const [enviando, setEnviando] = useState(null); // 'asaas' | 'transferencia' | null

  // Ao abrir (ou trocar de venda): mostra na hora o que veio com a lista e
  // em seguida busca os pagamentos atuais na api — o webhook do Asaas pode
  // ter marcado como pago depois que a lista carregou (PT384625, 22/10/2026).
  // Não recarrega a cada atualização da lista, senão o envio recém-feito
  // some da tela.
  useEffect(() => {
    setPayments((currentClient.payments || []).filter((p) => p.status !== CANCELLED).sort(ordenar));
    setLinksSentAt(currentClient.payment_links_sent_at || null);
    setMostrarPagos(false);

    if (!dialogPaymentsStatus || !currentClient.id) return undefined;

    let ativo = true;
    apiFetch(`salesscope/${currentClient.id}/payments`)
      .then((res) => {
        if (!ativo || !res) return;
        if (Array.isArray(res.payments)) {
          setPayments(res.payments.filter((p) => p.status !== CANCELLED).sort(ordenar));
        }
        if (res.payment_links_sent_at) setLinksSentAt(res.payment_links_sent_at);
      })
      .catch(() => {});

    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClient.id, dialogPaymentsStatus]);

  const pagos = payments.filter((p) => situacao(p).pago);
  const emAberto = payments.filter((p) => !situacao(p).pago);
  const visiveis = mostrarPagos ? payments : emAberto;
  const totalPago = pagos.reduce((soma, p) => soma + Number(p.value || 0), 0);
  const totalGeral = payments.reduce((soma, p) => soma + Number(p.value || 0), 0);

  const temAsaasEmAberto = emAberto.some((p) => METODOS_ASAAS.includes(p.payment_method));
  const temTransferenciaEmAberto = emAberto.some((p) => p.payment_method === PAYMENT_BANK_TRANSFER);
  const contratoAssinado = Boolean(currentClient.signed_date);

  const enviarLinksAsaas = () => {
    setEnviando('asaas');
    apiFetch(`salesscope/${currentClient.id}/asaas-checkout`)
      .then((res) => {
        props.showNotification(NOTIFICATION_TYPE_SUCCESS, res.message || 'Links de pagamento enviados.');
        if (Array.isArray(res.payments)) {
          setPayments(res.payments.filter((p) => p.status !== CANCELLED).sort(ordenar));
        }
        if (res.checkout_url) setLinksSentAt(new Date().toISOString());
        if (setUpdate) setUpdate(uniqid());
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Não foi possível enviar os links de pagamento.';
        props.showNotification(NOTIFICATION_TYPE_ERROR, msg);
      })
      .finally(() => setEnviando(null));
  };

  const reenviarDadosTransferencia = () => {
    setEnviando('transferencia');
    apiFetch(`salesscope/${currentClient.id}/send-bank-info-email`)
      .then(() => props.showNotification(NOTIFICATION_TYPE_SUCCESS, 'E-mail com os dados para transferência enviado.'))
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Não foi possível reenviar os dados de transferência.';
        props.showNotification(NOTIFICATION_TYPE_ERROR, msg);
      })
      .finally(() => setEnviando(null));
  };

  const colunaLink = (payment) => {
    if (payment.payment_document_url) {
      // "Enviado" quando o e-mail com os links já foi ao cliente; antes
      // disso o link existe no Asaas, mas o cliente ainda não o recebeu.
      return (
        <Typography component="span" variant="body2" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <Tooltip title={linksSentAt ? 'Link enviado ao cliente por e-mail' : 'Link gerado no Asaas; o cliente ainda não recebeu o e-mail'}>
            <Chip size="small" label={linksSentAt ? 'Enviado' : 'Gerado'} color={linksSentAt ? 'primary' : 'default'} variant="outlined" />
          </Tooltip>
          <LinkMaterial href={payment.payment_document_url} target="_blank" rel="noopener noreferrer">
            Abrir
          </LinkMaterial>
          <CopyElement value={payment.payment_document_url}>Copiar</CopyElement>
        </Typography>
      );
    }
    if (METODOS_ASAAS.includes(payment.payment_method)) {
      return (
        <Tooltip title={contratoAssinado ? 'Use "Enviar links por e-mail" para gerar a cobrança no Asaas' : 'O link é gerado quando o cliente assina o contrato'}>
          <Chip size="small" label="Link não gerado" variant="outlined" />
        </Tooltip>
      );
    }
    if (payment.payment_method === PAYMENT_BANK_TRANSFER) {
      return <Chip size="small" label="Dados por e-mail" variant="outlined" />;
    }
    return <Typography variant="body2" color="textSecondary">—</Typography>;
  };

  return (
    <Dialog
      PaperProps={{ square: true }}
      open={Boolean(dialogPaymentsStatus)}
      onClose={() => handleDialogPaymentsStatus()}
      scroll="body"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="payments-status-title">
        {`Pagamentos de ${currentClient.client_id || ''}`}
        {currentClient.customer && currentClient.customer.name ? ` — ${currentClient.customer.name}` : ''}
      </DialogTitle>
      <DialogContent>
        {payments.length === 0 ? (
          <DialogContentText>Nenhum pagamento cadastrado nesta venda.</DialogContentText>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <DialogContentText style={{ margin: 0 }}>
                {`${pagos.length} de ${payments.length} pagamento${payments.length > 1 ? 's' : ''} pago${pagos.length === 1 ? '' : 's'}`}
                {` · ${convertCurrency(totalPago)} de ${convertCurrency(totalGeral)}`}
              </DialogContentText>
              <FormControlLabel
                control={<Switch size="small" color="primary" checked={mostrarPagos} onChange={(e) => setMostrarPagos(e.target.checked)} />}
                label="Mostrar pagos"
              />
            </div>
            {visiveis.length === 0 ? (
              <DialogContentText style={{ marginTop: 16 }}>
                Nenhum pagamento em aberto. Todos os pagamentos desta venda estão pagos.
              </DialogContentText>
            ) : (
              <Table size="small" style={{ marginTop: 8 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Parcela</TableCell>
                    <TableCell>Forma</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell>Situação</TableCell>
                    <TableCell>Link de pagamento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiveis.map((payment) => {
                    const sit = situacao(payment);
                    return (
                      <TableRow key={payment.id || uniqid()}>
                        <TableCell>{parcelaLinha(payment)}</TableCell>
                        <TableCell>{paymentMethodTitles[payment.payment_method] || payment.payment_method}</TableCell>
                        <TableCell align="right">{valorLinha(payment)}</TableCell>
                        <TableCell>{payment.due_date ? localeDate(payment.due_date) : '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={sit.label} color={sit.cor} variant={sit.pago ? 'default' : 'outlined'} />
                        </TableCell>
                        <TableCell>{colunaLink(payment)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              {linksSentAt
                ? `E-mail com os links de pagamento enviado ao cliente em ${moment(linksSentAt).format('DD/MM/YYYY [às] HH:mm')}.`
                : 'O cliente ainda não recebeu o e-mail com os links de pagamento.'}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleDialogPaymentsStatus()} color="secondary" variant="outlined">Fechar</Button>
        {temTransferenciaEmAberto && (
          <Button
            onClick={reenviarDadosTransferencia}
            color="primary"
            variant="outlined"
            disabled={Boolean(enviando)}
          >
            {enviando === 'transferencia' ? 'Enviando...' : 'Reenviar dados de transferência'}
          </Button>
        )}
        {temAsaasEmAberto && (
          <Tooltip title={contratoAssinado
            ? 'Gera no Asaas as cobranças que ainda não existem e envia ao cliente o e-mail com os links'
            : 'Disponível depois que o cliente assinar o contrato'}
          >
            <span>
              <Button
                onClick={enviarLinksAsaas}
                color="primary"
                variant="contained"
                disableElevation
                disabled={Boolean(enviando) || !contratoAssinado}
              >
                {enviando === 'asaas' ? 'Enviando...' : 'Enviar links por e-mail'}
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default connect(null, { showNotification })(PaymentsStatusDialog);
